/**
 * The platform console's client.
 *
 * A separate module from api.ts, and deliberately so: nothing here sends an
 * `x-company` header, because a platform admin is not inside a company, and
 * nothing in api.ts can reach these routes by accident. The two consoles share
 * an error type and a fetch shape and nothing else.
 */
import { createContext, useContext } from 'react'
import { ApiError } from './api.js'

const BASE = '/api/platform'

export type Role = 'OWNER' | 'ADMIN' | 'MEMBER'

export interface PlatformSessionView {
  readonly adminId: string
  readonly name: string
  readonly email: string
  readonly mustChangePassword: boolean
}

export interface PlatformAdminView {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly active: boolean
  readonly mustChangePassword: boolean
  readonly lastLoginAt: string | null
  readonly lockedUntil: string | null
}

export interface PlatformTenantView {
  readonly id: string
  readonly slug: string
  readonly name: string
  readonly schemaName: string
  readonly active: boolean
  readonly timeZone: string
  readonly createdAt: string | null
  readonly companyCount: number
  readonly userCount: number
  readonly activeUserCount: number
}

export interface PlatformCompanyView {
  readonly id: string
  readonly tenantId: string
  readonly name: string
  readonly legalName: string
  readonly abbreviation: string
  readonly gstin: string | null
  readonly active: boolean
}

export interface PlatformUserView {
  readonly id: string
  readonly tenantId: string
  readonly tenantSlug: string
  readonly tenantName: string
  readonly tenantActive: boolean
  readonly name: string
  readonly email: string
  readonly role: Role
  readonly active: boolean
  readonly userType: string
  readonly mustChangePassword: boolean
  readonly lastLoginAt: string | null
  readonly lockedUntil: string | null
  /** Empty means every company in the tenant — the rule the server applies. */
  readonly companyIds: readonly string[]
  readonly companyNames: readonly string[]
}

export interface PlatformAuditView {
  readonly id: string
  readonly at: string
  readonly adminEmail: string
  readonly tenantSlug: string | null
  readonly entity: string
  readonly entityId: string
  readonly action: string
  readonly before: unknown
  readonly after: unknown
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = new Headers(init.headers)
  headers.set('accept', 'application/json')
  if (init.body !== undefined) headers.set('content-type', 'application/json')

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' })
  } catch {
    throw new ApiError(0, 'The server could not be reached. Check your connection and try again.', 'offline')
  }

  if (!res.ok) {
    let code = 'error'
    let message = `The server refused that request (${res.status}).`
    try {
      const body: unknown = await res.json()
      if (isRecord(body)) {
        if (typeof body['code'] === 'string') code = body['code']
        if (typeof body['message'] === 'string') message = body['message']
      }
    } catch { /* an empty or non-JSON body keeps the default wording */ }
    throw new ApiError(res.status, message, code)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const post = <T>(path: string, body: unknown = {}): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) })

const query = (params: Readonly<Record<string, string | undefined>>): string => {
  const usable = Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as Array<[string, string]>
  return usable.length === 0 ? '' : `?${new URLSearchParams(usable).toString()}`
}

export const platformApi = {
  login: (email: string, password: string) =>
    post<PlatformSessionView>('/login', { email, password }),
  logout: () => post<void>('/logout'),
  session: () => request<PlatformSessionView>('/session'),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<void>('/password', { currentPassword, newPassword }),

  admins: () => request<readonly PlatformAdminView[]>('/admins'),
  createAdmin: (name: string, email: string) =>
    post<{ admin: PlatformAdminView; temporaryPassword: string }>('/admins', { name, email }),
  setAdminActive: (id: string, active: boolean) =>
    post<readonly PlatformAdminView[]>(`/admins/${id}/active`, { active }),

  tenants: () => request<readonly PlatformTenantView[]>('/tenants'),
  createTenant: (input: {
    slug: string; name: string; companyName: string; abbreviation: string
    ownerName: string; ownerEmail: string
  }) => post<{ tenant: PlatformTenantView; owner: PlatformUserView; temporaryPassword: string }>('/tenants', input),
  setTenantActive: (id: string, active: boolean) =>
    post<readonly PlatformTenantView[]>(`/tenants/${id}/active`, { active }),

  companies: (tenantId: string) => request<readonly PlatformCompanyView[]>(`/tenants/${tenantId}/companies`),
  saveCompany: (tenantId: string, company: {
    id?: string; name: string; legalName: string; abbreviation: string
    gstin: string | null; active: boolean
  }) => post<readonly PlatformCompanyView[]>(`/tenants/${tenantId}/companies`, company),

  users: (q: { tenantId?: string; q?: string } = {}) =>
    request<readonly PlatformUserView[]>(`/users${query(q)}`),
  createUser: (input: { tenantId: string; name: string; email: string; role: Role; companyIds: readonly string[] }) =>
    post<{ user: PlatformUserView; temporaryPassword: string }>('/users', input),
  setUserRole: (id: string, role: Role) =>
    post<readonly PlatformUserView[]>(`/users/${id}/role`, { role }),
  setUserActive: (id: string, active: boolean) =>
    post<readonly PlatformUserView[]>(`/users/${id}/active`, { active }),
  resetUserPassword: (id: string) =>
    post<{ temporaryPassword: string }>(`/users/${id}/reset-password`),
  setUserCompanies: (id: string, companyIds: readonly string[]) =>
    post<readonly PlatformUserView[]>(`/users/${id}/companies`, { companyIds }),

  audit: (q: { tenantId?: string; limit?: number } = {}) =>
    request<readonly PlatformAuditView[]>(`/audit${query({
      tenantId: q.tenantId, limit: q.limit === undefined ? undefined : String(q.limit),
    })}`),
}

export type PlatformApi = typeof platformApi

const PlatformApiContext = createContext<PlatformApi>(platformApi)
export const PlatformApiProvider = PlatformApiContext.Provider
export const usePlatformApi = (): PlatformApi => useContext(PlatformApiContext)
